// ─── External Libraries ───────────────────────────────────────────────────────
import AddIcon from "@mui/icons-material/Add";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import Visibility from "@mui/icons-material/Visibility";
import { Box, FormControl, MenuItem, Select, Typography } from "@mui/material";
import { useState } from "react";

// ─── Business Logic ───────────────────────────────────────────────────────────
import { useQuotesCharts } from "../bussiness/charts/hooks/useQuoteCharts";
import { QuotesChart } from "../bussiness/charts/QuotesChart";

// ─── Common Components ────────────────────────────────────────────────────────
import { RiveByKey } from "@components/riveAnimation";
import { isBenefitForbiddenError } from "../common/WidgetBenefitBlockedState";
import { WidgetComponentCharts } from "../common/WidgetComponentCharts";
import { WidgetMenuActions } from "../common/WidgetMenuActions/WidgetMenuActions";

// ─── UI ───────────────────────────────────────────────────────────────────────
import { SASDashboardSelectStyles, SASMenuStyles, SASSMenuPropsStyles } from "../ui/styles/inputStyles";

// ─── Local Components ─────────────────────────────────────────────────────────
import { QuoteCreation } from "./quote-create-stepper/QuoteCreation";
import { QuotesSummary } from "./QuotesSummary"; // Nuevo componente para resumen
import { QuoteTable } from "./QuotesTable/QuoteTable";

// ─── Utils ────────────────────────────────────────────────────────────────────
import { useWidgetVariant } from "@components/jobs2/useWidgetVariant";
import { widgetSmallHeight } from "../../utils";

// ─────────────────────────────────────────────────────────────────────────────

const QUOTES_ACTIONS = [
  {
    label: "Add Quote",
    icon: <AddIcon />,
    modalComponent: <QuoteCreation />,
    modalKey: "Dashboard-QuotesAdd-Business",
  },
  {
    label: "See Quotes",
    icon: <Visibility />,
    modalComponent: <QuoteTable />,
    modalKey: "Dashboard-Quotes-Business",
  },
];

// ─────────────────────────────────────────────────────────────────────────────

export const QuotesWidget = ({ ...props }) => {
  const { data, isLoading, error } = useQuotesCharts();
  const isForbidden = isBenefitForbiddenError(error);

  // const { canUseNow: hasBasicMetricsBenefit } = useBenefit(EBenefitTypes.METRICS_BASIC);

  const [metric, setMetric] = useState("percent"); // "quantity" | "earning" | "percent"
  const [period, setPeriod] = useState("monthly"); // "daily" | "weekly" | "monthly" | "quarterly" | "yearly"
  const { ref, isLargeSlot } = useWidgetVariant(500);

  // ─── Sub-renders ─────────────────────────────────────────────────────────

  const renderChart = () => (
    // <BenefitOverlayGate
    //   blockedTooltip="You don't have permission to view this widget"
    //   benefit={EBenefitTypes.METRICS_BASIC}
    //   overlayLabel="Premium access"
    //   showMode={EShowMode.DISABLED}
    //   action={
    //     <Button
    //       component={Link}
    //       to="/business/plan-center"
    //       variant="contained"
    //       size="small"
    //       sx={{ textTransform: "none", borderRadius: 2 }}
    //     >
    //       Unlock this feature
    //     </Button>
    //   }
    // >
    <QuotesChart
      overview={data}
      period={period}
      metric={metric}
      isLoading={isLoading}
      error={error}
      // PASAMOS isLargeSlot explícitamente como prop
      forceBarMode={isLargeSlot}
    />
    // </BenefitOverlayGate>
  );

  const renderMetricSelector = () => (
    // <Box sx={{ mb: 4, filter: !hasBasicMetricsBenefit ? "blur(4px)" : "none", pointerEvents: !hasBasicMetricsBenefit ? "none" : "auto", transition: "filter 0.3s" }}>
    <FormControl size="small" sx={{ minWidth: 100 }} variant="outlined">
      <Select value={metric} onChange={(e) => setMetric(e.target.value)} sx={SASDashboardSelectStyles} MenuProps={SASSMenuPropsStyles}>
        {/* <MenuItem value="earning">Earning</MenuItem> */}
        <MenuItem value="percent">Percentage</MenuItem>
        <MenuItem value="quantity">Quantity</MenuItem>
      </Select>
    </FormControl>
    // </Box>
  );

  const renderPeriodSelector = () => (
    // <Box sx={{ mb: 4, filter: !hasBasicMetricsBenefit ? "blur(4px)" : "none", pointerEvents: !hasBasicMetricsBenefit ? "none" : "auto", transition: "filter 0.3s" }}>
    <FormControl size="small" sx={{ minWidth: 100, position: "relative", left: isLargeSlot ? 60 : "30px" }} variant="outlined">
      <Select value={period} onChange={(e) => setPeriod(e.target.value)} sx={SASDashboardSelectStyles} MenuProps={SASSMenuPropsStyles}>
        <MenuItem value="daily">Daily</MenuItem>
        <MenuItem value="weekly">Weekly</MenuItem>
        <MenuItem value="monthly">Monthly</MenuItem>
        <MenuItem value="quarterly">Quarterly</MenuItem>
        <MenuItem value="yearly">Yearly</MenuItem>
      </Select>
    </FormControl>
    // </Box>
  );
  // el boton

  const preQuotesTable = () => (
    <Box>
      <QuotesSummary />
    </Box>
  );

  const renderActions = () => <WidgetMenuActions actionList={QUOTES_ACTIONS} sx={SASMenuStyles} />;

  const [summaryDataState, setSummaryDataState] = useState({ hasData: null, loading: true });

  const chartHasData = () => {
    if (!data) return false;

    const metricKeyMap = {
      earning: "sales",
      percent: "percentage",
      quantity: "quantity",
    };
    const realMetric = metricKeyMap[metric] || "quantity";
    const statuses = ["revision", "approved", "declined"];
    const workTypes = ["residential", "commercial"];

    return workTypes.some((workType) => statuses.some((status) => Number(data?.[workType]?.[period]?.[realMetric]?.[status] || 0) > 0));
  };

  const hasChartData = chartHasData();
  const shouldShowNoData = !hasChartData && summaryDataState.hasData === false;
  // ─── SUB-RENDERS ─────────────────────────────────────────────────────────

  const renderNoData = () => (
    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" sx={{}}>
      {/* <Lottie options={lottieOptions} height={100} width={100} isClickToPauseDisabled /> */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          overflow: "hidden",
          marginTop: -8,
        }}
      >
        <RiveByKey riveKey="timesheets" boxSize={200} />
      </Box>
      <Typography variant="body2" sx={{ display: "flex", mt: -2, color: "text.secondary", textAlign: "center", fontSize: "0.85rem", opacity: 0.8 }}>
        No quotes registered yet
      </Typography>
    </Box>
  );

  const renderWidgetQuotes = () => (
    <Box
      ref={ref}
      sx={{
        display: "flex",

        flexDirection: isLargeSlot ? "row" : "column",
        width: "100%",
        height: "100%",
        maxHeight: "300px",
        gap: isLargeSlot ? 4 : 1,
        justifyContent: "space-between",
        alignItems: "center",
        overflow: "hidden",
        px: 1,
        // backgroundColor: "red",
      }}
    >
      {/* Contenedor del Chart */}
      <Box
        sx={{
          flex: isLargeSlot ? "1 1 35%" : "1 1 100%",
          width: "100%",
          height: "100%",
          display: "flex",
          justifyContent: "center",
          minWidth: 0,
          marginTop: isForbidden ? 0 : 4,
        }}
      >
        <QuotesChart
          overview={data}
          period={period}
          metric={metric}
          isLoading={isLoading}
          error={error}
          // PASAMOS isLargeSlot como prop
          forceBarMode={isLargeSlot}
        />
      </Box>

      {/* La tabla SOLO se renderiza si es grande y no está bloqueado */}
      {isLargeSlot && !isForbidden && (
        <Box sx={{ width: "60%" }}>
          <QuotesSummary onDataStateChange={setSummaryDataState} />
        </Box>
      )}
    </Box>
  );

  // ─── Render ──────────────────────────────────────────────────────────────

  return (
    <WidgetComponentCharts
      sxProp={{
        height: widgetSmallHeight,
        bgcolor: "var(--fx-surface-base, #FFFFFF)",
        position: "relative",
      }}
      swapyName="quotes"
      {...props}
      title="Quotes"
      description="Manage and optimize your quotes efficiently."
      icon={<RequestQuoteIcon />}
      data={isLoading ? renderChart() : shouldShowNoData ? renderNoData() : renderWidgetQuotes()}
      actions={renderActions()}
      bottomLeft={isForbidden || isLoading ? <Box /> : renderMetricSelector()}
      bottomRight={isForbidden || isLoading ? <Box /> : renderPeriodSelector()}
    />
  );
};
